import { useEffect, useState } from "react";
import api from "../../../../lib/api";
import { errorToastFromAxios } from "../../../../lib/supportToast";
import { FeeRow, ScopedFeeSummary } from "../shared";
import { toast } from "sonner";
import { Check, Sparkles, ChevronRight, DollarSign, Users } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// PricingRulesCard
//   Global default OR per-buyer override for the 3 optional dropship fees:
//   handling · rush handling · insurance. Saves to localStorage in this
//   first pass so it's instantly usable; hooks into /api/dropship/pricing-
//   rules once the backend endpoint lands. All 3 fees are optional — each
//   has its own on/off toggle. Product-level MSRP / MAP / wholesale are
//   NOT overridden here — those come from the Catalog app (source of truth).

export default function PricingRulesCard({ buyers, scopedBuyer, isAll = true }) {
  const COLLAPSE_KEY = "setup-section-open:setup-section-pricing";
  const defaultRules = {
    mode: "global", // "global" | "per_buyer"
    global: {
      handling: { on: true,  value: 1.20 },
      rush:     { on: false, value: 2.50 },
      insurance:{ on: false, value: 0.60 },
    },
    overrides: {}, // buyerId → { handling, rush, insurance }
  };
  const [rules, setRules] = useState(defaultRules);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [expandedBuyerId, setExpandedBuyerId] = useState(null);
  const [openCard, setOpenCard] = useState(() => {
    try { const v = localStorage.getItem(COLLAPSE_KEY); return v == null ? false : v === "1"; } catch { return false; }
  });
  const toggleCard = () => setOpenCard(v => {
    const next = !v;
    try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  // Load from backend on mount — falls back to defaults on 404 / no doc yet.
  useEffect(() => {
    api.get("/dropship/pricing-rules")
      .then(r => {
        const doc = r.data || {};
        setRules({
          mode: doc.mode || "global",
          global: doc.global || defaultRules.global,
          overrides: doc.overrides || {},
        });
      })
      .catch(() => { /* keep defaults */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (fn) => {
    setRules(r => {
      const next = fn(structuredClone(r));
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dropship/pricing-rules", rules);
      setDirty(false);
      toast.success("Pricing rules saved · applied on next daily invoice");
      // Auto-collapse the section after successful save (matches Agreement + Catalog mirror behaviour).
      setOpenCard(false);
      try { localStorage.setItem(COLLAPSE_KEY, "0"); } catch { /* ignore */ }
    } catch (e) {
      errorToastFromAxios(e, "Could not save pricing rules");
    } finally {
      setSaving(false);
    }
  };

  const setGlobalFee = (key, next) => patch(r => { r.global[key] = next; return r; });
  const setOverrideFee = (bid, key, next) => patch(r => {
    if (!r.overrides[bid]) r.overrides[bid] = structuredClone(r.global);
    r.overrides[bid][key] = next;
    return r;
  });
  const removeOverride = (bid) => patch(r => { delete r.overrides[bid]; return r; });

  // ── Per-buyer scoped variant ────────────────────────────────────────
  // When a specific buyer is selected in the sidebar, we don't show the
  // full mode toggle + global-default + all-buyers list. Instead: a
  // compact card scoped to just this buyer, with a "Customize for this
  // buyer" button when using the default.
  if (!isAll && scopedBuyer) {
    const hasOverride = !!rules.overrides[scopedBuyer.id];
    const cfg = rules.overrides[scopedBuyer.id] || rules.global;
    return (
      <div className="rounded-2xl border border-border bg-white" data-testid="pricing-rules-card">
        <div
          className={`flex items-center gap-3 px-5 py-4 ${openCard ? "border-b border-border" : ""} cursor-pointer hover:bg-secondary/30`}
          onClick={toggleCard}>
          <DollarSign className="w-4 h-4 text-foreground/60 shrink-0"/>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold">Step 2 · Pricing rules</div>
            {openCard && (
              <div className="text-[11.5px] text-foreground/55 mt-0.5">
                {scopedBuyer.company || scopedBuyer.name} — {hasOverride
                  ? "custom fees apply only to this buyer. Product MSRP / MAP / wholesale still come from the Catalog app."
                  : "using global default. Click Customize to set different fees for this buyer only."}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {openCard && !hasOverride && (
              <button
                onClick={(e) => { e.stopPropagation(); patch(r => { r.overrides[scopedBuyer.id] = structuredClone(r.global); return r; }); }}
                data-testid={`pricing-scoped-customize-${scopedBuyer.id}`}
                className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90"
              >
                <Sparkles className="w-3.5 h-3.5"/> Customize for this buyer
              </button>
            )}
            {openCard && hasOverride && dirty && (
              <button onClick={(e) => { e.stopPropagation(); save(); }} disabled={saving} data-testid="pricing-rules-save"
                className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
                <Check className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save"}
              </button>
            )}
            <ChevronRight className={`w-4 h-4 text-foreground/40 transition-transform ${openCard ? "rotate-90" : ""}`}/>
          </div>
        </div>
        {openCard && (
        <div className="px-5 py-4">
          {!hasOverride ? (
            <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-6">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 mb-2">Currently using global default</div>
              <div className="divide-y divide-border">
                <ScopedFeeSummary label="Dropship handling fee" cfg={rules.global.handling} />
                <ScopedFeeSummary label="Rush handling"         cfg={rules.global.rush} />
                <ScopedFeeSummary label="Shipping insurance"    cfg={rules.global.insurance} />
              </div>
            </div>
          ) : (
            <>
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60 mb-1">Custom fees for this buyer</div>
              <div className="divide-y divide-border">
                <FeeRow label="Dropship handling fee" hint="Flat per-order fee for this buyer only."
                  cfg={cfg.handling}  onChange={next => setOverrideFee(scopedBuyer.id, "handling", next)} />
                <FeeRow label="Rush handling"         hint="Extra fee for same-day / next-day fulfilment."
                  cfg={cfg.rush}      onChange={next => setOverrideFee(scopedBuyer.id, "rush", next)} />
                <FeeRow label="Shipping insurance"    hint="Per-order insurance for damaged / lost shipments."
                  cfg={cfg.insurance} onChange={next => setOverrideFee(scopedBuyer.id, "insurance", next)} />
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => removeOverride(scopedBuyer.id)}
                  className="text-[11px] text-foreground/60 hover:text-rose-600 underline underline-offset-2"
                  data-testid={`pricing-scoped-reset-${scopedBuyer.id}`}>
                  Reset to global default
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>
    );
  }

  // ── Aggregate variant (all buyers) ──────────────────────────────────
  return (
    <div className="rounded-2xl border border-border bg-white" data-testid="pricing-rules-card">
      <div
        className={`flex items-center gap-3 px-5 py-4 ${openCard ? "border-b border-border" : ""} cursor-pointer hover:bg-secondary/30`}
        onClick={toggleCard}
        data-testid="setup-section-pricing-header">
        <DollarSign className="w-4 h-4 text-foreground/60 shrink-0"/>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold">Step 2 · Pricing rules</div>
          {openCard && (
            <div className="text-[11.5px] text-foreground/55 mt-0.5">
              Product MSRP / MAP / wholesale come from your <span className="font-medium text-foreground/80">Catalog app</span> (source of truth). Set optional dropship fees here — global default or per-buyer overrides.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {dirty && openCard && (
            <button onClick={(e) => { e.stopPropagation(); save(); }} disabled={saving} data-testid="pricing-rules-save"
              className="inline-flex items-center gap-1.5 rounded-full h-8 px-4 text-[11.5px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
              <Check className="w-3.5 h-3.5"/>{saving ? "Saving…" : "Save changes"}
            </button>
          )}
          <ChevronRight className={`w-4 h-4 text-foreground/40 transition-transform ${openCard ? "rotate-90" : ""}`}/>
        </div>
      </div>

      {openCard && <>
      {/* Mode toggle — global vs per-buyer */}
      <div className="flex gap-2 px-5 pt-4">
        {[
          { k: "global",    l: "Same for all buyers", d: "One set of fees applied to every buyer." },
          { k: "per_buyer", l: "Per-buyer overrides", d: "Global default + custom fees per buyer that opt-in." },
        ].map(m => (
          <button key={m.k}
            onClick={() => patch(r => { r.mode = m.k; return r; })}
            data-testid={`pricing-mode-${m.k}`}
            className={`flex-1 text-left rounded-xl border px-4 py-3 transition-colors ${
              rules.mode === m.k
                ? "border-foreground bg-foreground/[0.02]"
                : "border-border hover:bg-secondary/40"
            }`}>
            <div className="flex items-center gap-2 text-[13px] font-medium">
              <div className={`w-4 h-4 rounded-full border-2 grid place-items-center ${rules.mode === m.k ? "border-foreground" : "border-border"}`}>
                {rules.mode === m.k && <div className="w-1.5 h-1.5 rounded-full bg-foreground"/>}
              </div>
              {m.l}
            </div>
            <div className="text-[11px] text-foreground/55 mt-1 pl-6">{m.d}</div>
          </button>
        ))}
      </div>

      {/* Global fees always shown (they're the default even in per-buyer mode) */}
      <div className="px-5 py-4">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60 mb-1">Global default</div>
        <div className="divide-y divide-border">
          <FeeRow
            label="Dropship handling fee"
            hint="Flat per-order fee added to each dropship shipment."
            cfg={rules.global.handling}
            onChange={next => setGlobalFee("handling", next)}
          />
          <FeeRow
            label="Rush handling"
            hint="Extra fee for same-day / next-day fulfilment."
            cfg={rules.global.rush}
            onChange={next => setGlobalFee("rush", next)}
          />
          <FeeRow
            label="Shipping insurance"
            hint="Per-order insurance for damaged / lost shipments."
            cfg={rules.global.insurance}
            onChange={next => setGlobalFee("insurance", next)}
          />
        </div>
      </div>

      {/* Per-buyer overrides — only when mode is "per_buyer" */}
      {rules.mode === "per_buyer" && (
        <div className="px-5 pb-5 border-t border-border">
          <div className="pt-4 pb-3 flex items-center justify-between">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60">Per-buyer overrides</div>
            <div className="text-[11px] text-foreground/55">
              {Object.keys(rules.overrides).length} of {buyers.length} customized
            </div>
          </div>

          {buyers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-6 text-center text-[12px] text-foreground/55">
              Once you have connected buyers, they&apos;ll appear here to customize per-buyer fees.
            </div>
          ) : (
            <div className="rounded-xl border border-border overflow-hidden">
              {buyers.map(b => {
                const hasOverride = !!rules.overrides[b.id];
                const isExpanded  = expandedBuyerId === b.id;
                const cfg = rules.overrides[b.id] || rules.global;
                return (
                  <div key={b.id} className="border-b border-border last:border-b-0" data-testid={`pricing-override-${b.id}`}>
                    <button
                      onClick={() => setExpandedBuyerId(isExpanded ? null : b.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors text-left">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 grid place-items-center shrink-0">
                        <Users className="w-3.5 h-3.5 text-indigo-700"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">{b.company || b.name}</div>
                        <div className="text-[11px] text-foreground/55 truncate">
                          {hasOverride
                            ? `Custom · handling ${cfg.handling.on ? "$" + cfg.handling.value : "off"} · rush ${cfg.rush.on ? "$" + cfg.rush.value : "off"} · insurance ${cfg.insurance.on ? "$" + cfg.insurance.value : "off"}`
                            : "Using global default"}
                        </div>
                      </div>
                      {hasOverride && (
                        <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Overridden</span>
                      )}
                      <ChevronRight className={`w-3.5 h-3.5 text-foreground/40 transition-transform ${isExpanded ? "rotate-90" : ""}`}/>
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 bg-secondary/20 border-t border-border">
                        <div className="divide-y divide-border/60">
                          <FeeRow
                            label="Dropship handling fee"
                            hint="Override the global handling fee for this buyer only."
                            cfg={cfg.handling}
                            onChange={next => setOverrideFee(b.id, "handling", next)}
                          />
                          <FeeRow
                            label="Rush handling"
                            hint="Override rush fee for this buyer only."
                            cfg={cfg.rush}
                            onChange={next => setOverrideFee(b.id, "rush", next)}
                          />
                          <FeeRow
                            label="Shipping insurance"
                            hint="Override insurance fee for this buyer only."
                            cfg={cfg.insurance}
                            onChange={next => setOverrideFee(b.id, "insurance", next)}
                          />
                        </div>
                        {hasOverride && (
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => removeOverride(b.id)}
                              className="text-[11px] text-foreground/60 hover:text-rose-600 underline underline-offset-2"
                              data-testid={`pricing-override-clear-${b.id}`}>
                              Reset to global default
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </>}
    </div>
  );
}
